import { valueToBigNumber } from '@aave/math-utils';
import BigNumber from 'bignumber.js';
import { ComputedReserveData, ComputedUserReserveWithLimits } from '../libs/pool-data-provider';

export enum BorrowUnavailableReason {
  HealthFactorTooLow = 'health-factor-too-low',
  ZeroLoanToValue = 'zero-loan-to-value',
  BorrowingPowerFullyUsed = 'borrowing-power-fully-used',
  MarketLimitReached = 'market-limit-reached',
}

interface BorrowUnavailableReasonParams {
  rawMaxAmountToBorrow: BigNumber;
  maxAmountToBorrow: BigNumber;
  currentLoanToValue: string;
  healthFactor: string;
  totalBorrowsMarketReferenceCurrency: string;
}

/**
 * Classifies why no borrow amount is available without conflating LTV capacity with liquidation risk.
 */
export function getBorrowUnavailableReason({
  rawMaxAmountToBorrow,
  maxAmountToBorrow,
  currentLoanToValue,
  healthFactor,
  totalBorrowsMarketReferenceCurrency,
}: BorrowUnavailableReasonParams): BorrowUnavailableReason | undefined {
  if (maxAmountToBorrow.gt(0)) {
    return undefined;
  }
  if (rawMaxAmountToBorrow.gt(0)) {
    return BorrowUnavailableReason.MarketLimitReached;
  }

  // Zero LTV blocks new debt even when a positive liquidation threshold keeps health factor healthy.
  if (valueToBigNumber(currentLoanToValue).lte(0)) {
    return BorrowUnavailableReason.ZeroLoanToValue;
  }

  const hasDebt = valueToBigNumber(totalBorrowsMarketReferenceCurrency).gt(0);
  const currentHealthFactor = valueToBigNumber(healthFactor);
  if (hasDebt && currentHealthFactor.gte(0) && currentHealthFactor.lte(1)) {
    return BorrowUnavailableReason.HealthFactorTooLow;
  }
  return BorrowUnavailableReason.BorrowingPowerFullyUsed;
}

export function getMaxAmountToDeposit(
  walletBalance: BigNumber,
  reserve: ComputedReserveData,
  userReserve: ComputedUserReserveWithLimits | undefined
): BigNumber {
  let maxAmountToDeposit = walletBalance;
  if (
    reserve.hasMaxGlobalDepositSizeLimit &&
    maxAmountToDeposit.gt(valueToBigNumber(reserve.globalDepositSizeAvailable))
  ) {
    maxAmountToDeposit = valueToBigNumber(reserve.globalDepositSizeAvailable);
  }
  if (
    reserve.hasMaxIndividualDepositSizeLimit &&
    userReserve &&
    maxAmountToDeposit.gt(valueToBigNumber(userReserve.individualDepositSizeAvailable))
  ) {
    maxAmountToDeposit = valueToBigNumber(userReserve.individualDepositSizeAvailable);
  }
  return maxAmountToDeposit;
}

export function getMaxAmountToBorrow(
  availableBorrows: BigNumber,
  reserve: ComputedReserveData,
  userReserve: ComputedUserReserveWithLimits | undefined
): BigNumber {
  let maxAmountToBorrow = availableBorrows;
  if (
    reserve.hasMaxGlobalBorrowSizeLimit &&
    maxAmountToBorrow.gt(valueToBigNumber(reserve.globalBorrowSizeAvailable))
  ) {
    maxAmountToBorrow = valueToBigNumber(reserve.globalBorrowSizeAvailable);
  }
  if (
    reserve.hasMaxIndividualBorrowSizeLimit &&
    userReserve &&
    maxAmountToBorrow.gt(valueToBigNumber(userReserve.individualBorrowSizeAvailable))
  ) {
    maxAmountToBorrow = valueToBigNumber(userReserve.individualBorrowSizeAvailable);
  }
  if (maxAmountToBorrow.lte(0)) {
    maxAmountToBorrow = valueToBigNumber('0');
  }
  return maxAmountToBorrow;
}
